import { useNavigate } from 'react-router-dom'

import { Form } from 'factorial-form'
import moment from 'moment'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import { dateToString, dateUIToBackend } from 'modules/core/lib/dates'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import PlanningVersionsApi from 'modules/time_planning/store/collections/planningVersions'
import { PLANNING_TOOL } from 'modules/time_planning/store/models/PlanningVersion'
import DeletePeriod from 'modules/work_schedules/components/DeletePeriodModal'
import { canReassignAllEmployees } from 'modules/work_schedules/lib/canAssignEmployees'
import overlapPeriods from 'modules/work_schedules/store/collections/overlapPeriods'
import schedules from 'modules/work_schedules/store/collections/schedules'

const DeletePeriodModalPage = () => {
  const { navigation } = useNavigation()
  const { id, periodId } = useStrictParams<'id' | 'periodId'>()
  const navigate = useNavigate()
  const period = overlapPeriods.get(Number(periodId))

  const form = useLazyRef(
    () =>
      new Form(
        {
          effective_at: dateToString(moment(), 'backend'),
        },
        {
          work_schedule_id: 'string',
          effective_at: 'date',
        }
      )
  ).current

  const workScheduleId = period?.attributes.schedule_id
  const workSchedule = schedules.get(workScheduleId)
  const workScheduleEmployeesIds = workSchedule?.attributes.employees.slice()
  const noOfEmployees = workScheduleEmployeesIds ? workScheduleEmployeesIds.length : 0

  const canReassignAll = workSchedule
    ? canReassignAllEmployees(workSchedule, noOfEmployees, contractVersions)
    : false

  const canArchive = !noOfEmployees || canReassignAll

  const archivePeriod = async (closePortal: ClosePortalFn) => {
    if (!canArchive || !period) {
      return
    }

    // In the case that there are no employees assigned to it
    if (noOfEmployees < 1) {
      const result = await asyncAction(() => period.destroy())

      if (isSuccessful(result)) {
        closePortal()

        return
      }
    }

    // get the WS you want to reassign the employees to
    const selectedWorkScheduleId = form.get('work_schedule_id').value
    const effectiveAt = dateUIToBackend(form.get('effective_at').value)

    const result = await asyncAction(() =>
      // reassign the archived WS employees to the selected WS by creating new PV
      PlanningVersionsApi.rpc('bulk_create', {
        employee_ids: workScheduleEmployeesIds,
        effective_at: effectiveAt,
        planning_tool: PLANNING_TOOL.workSchedule,
        schedule_id: +selectedWorkScheduleId,
      })
    )

    if (isSuccessful(result)) {
      // archive WS
      await workSchedule?.rpc('toggle_archive')

      workScheduleEmployeesIds?.map((id) => {
        const employee = employees.get(id)
        employee?.set({
          effective_at: effectiveAt,
        })
      })

      closePortal({
        preventOnCloseCallbackAfterClose: true,
      })
      navigate(navigation.settings.workSchedule.fullPath())
    }
  }

  const getOptions = () => {
    return schedules.models
      .filter(
        (schedule) =>
          schedule.attributes.id !== workScheduleId && schedule.attributes.archived_at === null
      )
      .map((schedule) => ({
        label: schedule.attributes.name,
        value: schedule.attributes.id,
      }))
  }

  const options = !schedules ? [] : getOptions()

  const onClose = () => {
    navigate(navigation.settings.workSchedule.id.fullPath({ id }))
  }

  const { loading } = useAsyncData(
    () => contractVersions.fetch({ withCachedFetchParams: true }),
    []
  )

  return (
    <DeletePeriod
      noOfEmployees={noOfEmployees}
      onClose={onClose}
      onConfirm={archivePeriod}
      options={options}
      form={form}
      canArchive={canArchive}
      canReassignAll={canReassignAll}
      contractVersions={contractVersions}
      loading={loading}
    />
  )
}

export default DeletePeriodModalPage
